import React, { useEffect, useState } from "react";
import { Button, Box, Card, Typography, Modal, useMediaQuery } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CustomDataTable,
  TableColumn,
} from "../../components/CommnTable/CustomDataTable";
import ViewIcon from "../../assets/icons/view-icon.svg";
import styles from "./ApplicationReview.module.scss";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Chip } from "@mui/material";

import BrightnessLowIcon from "@mui/icons-material/BrightnessLow";
import { useMutationByMethod } from "../../services/hooks/useMutationByMethod";
import { GetUrl, PostUrl } from "../../api/endpointConfig";
import { useGet } from "../../services/hooks/useGet";
import { ApplicantInterface } from "../../types/types";
import { RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { addFundallowItem, addRowDataItem } from "../../redux/actions/Actions";
import { useLoader } from "../../Loader/LoaderContext";
import { statusOptions } from "../../data/alldata";
import EditIcon from '@mui/icons-material/Edit';


const getStatusChipColor = (status: string) => {
  switch (status) {
    case "approved":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "error";
    case "document_submitted":
      return "info";
    case "pending_additional_documents":
      return "secondary";
    case "scheduled_interview":
      return "primary";
    case "draft":
      return "default";
    default:
      return "default";
  }
};

const ApplicationReview: React.FC = () => {
  const [searchParams] = useSearchParams();


  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [offsetValue, setOffsetValue] = useState<number>(0);
  const [limitValue, setLimitValue] = useState<number>(20);
 
  const [mainData, setMainData] = useState<ApplicantInterface[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [queryParams, setQueryParams] = useState<any>({})
  const pagination = {
    offset: 0,
    limit: 0,
  }
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();

  

   const isDesktop = useMediaQuery("(min-width:1025px)");

  // ✅ Reset Redux ONLY once (not on pagination)
  useEffect(() => {
    dispatch(addFundallowItem(null as any) as any);
  }, [dispatch]);

  // ✅ Sync URL params → queryParams
  useEffect(() => {
    const prefix = searchParams.get("prefix");
    const text = searchParams.get("text");

    if (text && prefix) {
      setQueryParams({ [prefix]: text });
    } else {
      setQueryParams({});
    }
  }, [searchParams]);

  // ✅ API Hook (single source of truth)
  const getApplicationData = useGet<any>(GetUrl.getApplicationAll, {
    params: {
      offset: 0,
      limit: 0,
      ...queryParams,
    },
  });

  // ✅ Loader handling
  useEffect(() => {
    if (getApplicationData.isLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [getApplicationData.isLoading]);

  // ✅ Data binding
  useEffect(() => {
    if (getApplicationData.data?.data) {
      setMainData(getApplicationData.data.data);
    }
  }, [getApplicationData.data]);

  // ✅ Pagination
  const handlePageChange = (event: any) => {
    setOffsetValue(event.page * event.rows);
    setLimitValue(event.rows);
  };

  const setClearFilterState = (state: boolean) => {
    if (state) setQueryParams({});
  };
  
  const columns: TableColumn<ApplicantInterface>[] = [
    {
      field: "application_id",
      header: "App ID",
      style: { whiteSpace: "nowrap" },
      frozen: isDesktop ? true : false,
      alignFrozen: "left",
      sortable: true,
      filterable: true
    },
    { field: "name", header: "Name", style: { whiteSpace: "nowrap" }, sortable: true, filterable: true },
    { field: "phone_number", header: "Phone", style: { whiteSpace: "nowrap" }, sortable: true, filterable: true },
    { field: "email", header: "Email", style: { whiteSpace: "nowrap" }, sortable: true, filterable: true },
    {
      field: "status",
      header: "Status",
      style: { whiteSpace: "nowrap" },
      sortable: true,
      filterable: true,
      customBody: (rowData) => (
        <Chip
          label={
            statusOptions.find((m: any) => m.value === rowData.status)?.label
          }
          color={getStatusChipColor(rowData.status)}
          size="small"
          variant="filled"
          style={{ fontWeight: 600, textTransform: "capitalize" }}
        />
      ),
    },
    {
      field: "college_name",
      header: "College/School",
      style: { whiteSpace: "nowrap" },
      sortable: true,
      filterable: true
    },
    { field: "district", header: "District", style: { whiteSpace: "nowrap" }, sortable: true, filterable: true },
    { field: "state", header: "State", style: { whiteSpace: "nowrap" }, sortable: true, filterable: true },
    {
      field: "achivements",
      header: "Achievement",
      style: { whiteSpace: "nowrap" },
      sortable: true,
      customBody: (rowData) => (
        <Chip
          label={rowData.achivements ? "Yes" : "No"}
          color={rowData.achivements ? "success" : "default"}
          size="small"
          variant="filled"
          style={{ fontWeight: 600 }}
        />
      ),
    },
    { field: "stream", header: "Stream", style: { whiteSpace: "nowrap" }, sortable: true, filterable: true },
    {
      field: "viewapp",
      header: "Application",
      style: { whiteSpace: "nowrap" },
      sortable: false,
      filter: false,
      customBody: (rowData) => (
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={() => {
            navigate(`/application-management-view/${rowData.application_id}`);
            dispatch(addRowDataItem(rowData) as any);
          }}
          className="commonBtnOne"
          startIcon={<VisibilityOutlinedIcon />}
        >
          View
        </Button>
      ),
      frozen: isDesktop ? true : false,
      alignFrozen: isDesktop ? "right" : undefined,
    },
    {
      field: "evform",
      header: "Evaluation Form",
      style: { whiteSpace: "nowrap" },
      sortable: false,
      filter: false,
      customBody: (rowData) => (
        <Button
          size="small"
          variant="text"
          color="success"
          onClick={() => {
            navigate(`/evaluation/${rowData.application_id}`);
            dispatch(addRowDataItem(rowData) as any);
          }}
          className="commonBtnTwo"
          startIcon={(rowData.status === "scheduled_interview" || rowData.status === "pending") ? <EditIcon /> : <VisibilityOutlinedIcon />}
          disabled={
            rowData.status === "pending" ||
              rowData.status === "rejected" ||
              rowData.status === "pending_additional_documents" ||
              rowData.status === "pending_documents" ||
              rowData.status === "document_submitted" ||
              rowData.status === "draft"
              ? true
              : false
          }
        >
          {(rowData.status === "scheduled_interview" || rowData.status === "pending") ? "Initiate" : "View"}
        </Button>
      ),
      frozen: isDesktop ? true : false,
      alignFrozen: isDesktop ? "right" : undefined,
    },
  ];
 

  return (
    <>
      <Card
        style={{
          padding: "1rem",
          border: 0,
          boxShadow: "none",
          background: "none",
        }}
      >
        <Box className={"cardBoxUI"}>
          <CustomDataTable<ApplicantInterface>
            data={mainData}
            columns={columns}
            tableTitle="Application Review"
            rowsPerPage={limitValue}
            rowCount={getApplicationData.data?.total ?? 0}
            first={offsetValue}      // 🔑 Add this so DataTable syncs with state
            rows={limitValue}
            onPageChange={handlePageChange}
            onSearchClear={() => setQueryParams({})}
            exportExcelShow={true}
            clearFilter={setClearFilterState}
          />
        </Box>
      </Card>
    </>
  );
};

export default ApplicationReview;
